import React, { FormEvent, useEffect, useMemo, useState } from 'react'
import { Button, Col, Form, Row } from 'react-bootstrap'
import { URLSearchParamsInit, useSearchParams } from 'react-router-dom';
import { getNumberByConditionOrEmptyString, getStringByConditionOrEmptyString } from '../../helpers/getNumberByConditionOrType';
import { isPostalCodeValid, isValidForRegex } from '../../helpers/regexValidity';
import { ClubSearchInput } from '../../types/club/ClubSearchInput';

interface Props {
    sports: string[]
    fetchClubs: (inputs: ClubSearchInput) => void
    reset: () => void
    className?: string
    setError: (error: string) => void
}

const perimeters = [1, 2, 3, 4, 5, 10];

export const ClubInputs: React.FC<Props> = ({ sports, fetchClubs, reset, className, setError }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [club, setClub] = useState(searchParams.get("clubName") || "");
    const [sport, setSport] = useState(getStringByConditionOrEmptyString(searchParams.get("sportName"), sportName => isValidForRegex(sportName, /^[a-zA-Z ]+$/)));
    const [postalCode, setPostalCode] = useState(getStringByConditionOrEmptyString(searchParams.get("postalCode"), postalCode => isPostalCodeValid(postalCode)));
    const [perimeter, setPerimeter] = useState(getNumberByConditionOrEmptyString(searchParams.get("perimeter"), perimeter => perimeter >= 0) + "");
    const fetchQuery = useMemo(() => ({
        clubName: club,
        sportName: sport,
        postalCode,
        perimeter: perimeter ? parseInt(perimeter) : undefined
    }) as ClubSearchInput, [club, sport, postalCode, perimeter]);

    useEffect(() => {
        fetchClubs(fetchQuery);
        //can ignore this warning because we want the useEffect to execute only once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const validateAndSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (isFormValid()) {
            fetchClubs(fetchQuery);
            setSearchParams(convertToSearchParams(fetchQuery));
        } else {
            reset();
            updateError();
        }
    }

    const isFormValid = () => {
        let isValid = true;
        if (postalCode && !isPostalCodeValid(postalCode)) {
            isValid = false;
        } else if (perimeter && !(postalCode && isPostalCodeValid(postalCode))) {
            isValid = false;
        }
        return isValid;
    }

    const updateError = () => {
        //if perimeter is given but postal code doesn't have a length of 5
        if (!isPostalCodeValid(postalCode)) {
            setError("Geben Sie eine 5-stellige Postleitzahl ein");
        }
        else if (perimeter && !isPostalCodeValid(postalCode)) {
            setError("Geben Sie eine 5-stellige Postleitzahl ein");
        }
    }

    return (
        <Form className={className + " club-form form"} onSubmit={validateAndSubmit}>
            <Row className="input-row">
                <Form.Group as={Col} className="club-name-div form-group mb-2">
                    <Form.Label className="club-name-label form-label">Verein</Form.Label>
                    <Form.Control className="club-name-input form-input" type="text" onChange={event => setClub(event.currentTarget.value)} value={club} />
                </Form.Group>
                <Form.Group as={Col} className="club-sport-div form-group mb-2" sm>
                    <Form.Label className="club-sport-label form-label">Sportart</Form.Label>
                    <Form.Select className="club-sport-select form-select" onChange={event => setSport(event.currentTarget.value)} value={sport}>
                        <option value=""></option>
                        {sports.map((sport, index) => (
                            <option key={index}>{sport}</option>
                        ))}
                    </Form.Select>
                </Form.Group>
                <Col lg>
                    <Row>
                        <Form.Group as={Col} className="club-postal-code-div form-group mb-2" sm>
                            <Form.Label className="club-postal-code-label form-label">Postleitzahl</Form.Label>
                            <Form.Control
                                type="text"
                                onChange={event => setPostalCode(event.currentTarget.value)}
                                value={postalCode}
                                className="club-postal-code-input form-input"
                            />
                        </Form.Group>
                        <Form.Group as={Col} className="club-perimeter-div form-group mb-2" sm>
                            <Form.Label className="club-perimeter-label form-label">Umkreis</Form.Label>
                            <Form.Select className="club-perimeter-select form-select" onChange={event => setPerimeter(event.currentTarget.value)} value={perimeter}>
                                <option value=""></option>
                                {perimeters.map((perimeter, index) => (
                                    <option key={index} value={perimeter}>{perimeter} km</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group as={Col} className="club-search-button-div form-group col mt-3 mt-sm-0 align-self-end mb-2" style={{ flex: 0 }}>
                            <Button className="club-search-button form-button" variant="primary" type="submit">Suchen</Button>
                        </Form.Group>
                    </Row>
                </Col>
            </Row>
        </Form>
    )
}

//convert inputs to a valid URLSearchParamsInit Object: Record<String, String | String[]>
const convertToSearchParams = (input: ClubSearchInput) => {
    const URLSearchParamsInit: URLSearchParamsInit = {};
    if (input.sportName) URLSearchParamsInit.sportName = input.sportName;
    if (input.postalCode) URLSearchParamsInit.postalCode = input.postalCode;
    if (input.perimeter) URLSearchParamsInit.perimeter = input.perimeter + "";
    if (input.clubName) URLSearchParamsInit.clubName = input.clubName;
    return URLSearchParamsInit;
}
